/**
 * Plan-based feature gating service
 * Enforces subscription plan limits and feature access
 */

import { getUserPlan, getUserDocument } from './userService';
import { getUserHistory } from './historyService';

/**
 * Plan feature limits configuration
 * IMPORTANT: Free plan is limited to 3 videos total
 * All new users default to 'free' plan
 */
const PLAN_LIMITS = {
  free: {
    maxVideos: 3, // Free plan: 3 videos maximum
    favouritesEnabled: false,
    channelAnalysisEnabled: false,
    recentlyAnalyzedEnabled: false,
  },
  pro: {
    maxVideos: Infinity, // Unlimited
    favouritesEnabled: true,
    channelAnalysisEnabled: false,
    recentlyAnalyzedEnabled: true,
  },
  growth: {
    maxVideos: Infinity, // Unlimited
    favouritesEnabled: true,
    channelAnalysisEnabled: true,
    recentlyAnalyzedEnabled: true,
  },
};

/**
 * Gets the plan limits for a given plan
 * @param {string} plan - User's plan ('free', 'pro', or 'growth')
 * @returns {Object} Plan limits object
 */
export function getPlanLimits(plan) {
  const normalizedPlan = (plan || 'free').toLowerCase();
  return PLAN_LIMITS[normalizedPlan] || PLAN_LIMITS.free;
}

/**
 * Checks if user can analyze a new video based on their plan
 * @param {string} userId - User ID
 * @returns {Promise<{allowed: boolean, currentCount: number, maxCount: number, error?: string}>}
 */
export async function canAnalyzeVideo(userId) {
  if (!userId) {
    return {
      allowed: false,
      currentCount: 0,
      maxCount: 0,
      error: 'User ID is required',
    };
  }

  try {
    // Get user plan (defaults to 'free' if not set)
    const plan = await getUserPlan(userId);
    // Ensure we always have a valid plan (defaults to 'free')
    const normalizedPlan = (plan || 'free').toLowerCase();
    const limits = getPlanLimits(normalizedPlan);

    // If unlimited, always allow
    if (limits.maxVideos === Infinity) {
      return {
        allowed: true,
        currentCount: 0,
        maxCount: Infinity,
      };
    }

    // Get current video count
    const history = await getUserHistory(userId, 1000); // Get all videos to count
    const currentCount = history.length;

    const allowed = currentCount < limits.maxVideos;

    return {
      allowed,
      currentCount,
      maxCount: limits.maxVideos,
      error: allowed
        ? null
        : `You've reached the limit of ${limits.maxVideos} videos on the ${normalizedPlan} plan. Upgrade to Pro or Growth for unlimited videos.`,
    };
  } catch (error) {
    console.error('Error checking video analysis limit:', error);
    return {
      allowed: false,
      currentCount: 0,
      maxCount: 0,
      error: 'Failed to check plan limits. Please try again.',
    };
  }
}

/**
 * Checks if user can use favourites feature
 * @param {string} userId - User ID
 * @returns {Promise<{allowed: boolean, error?: string}>}
 */
export async function canUseFavourites(userId) {
  if (!userId) {
    return {
      allowed: false,
      error: 'User ID is required',
    };
  }

  try {
    const plan = await getUserPlan(userId);
    const limits = getPlanLimits(plan);

    return {
      allowed: limits.favouritesEnabled,
      error: limits.favouritesEnabled
        ? null
        : `Favourites are only available on Pro and Growth plans. Upgrade to unlock this feature.`,
    };
  } catch (error) {
    console.error('Error checking favourites access:', error);
    return {
      allowed: false,
      error: 'Failed to check plan limits. Please try again.',
    };
  }
}

/**
 * Checks if user can use channel analysis feature
 * @param {string} userId - User ID
 * @returns {Promise<{allowed: boolean, error?: string}>}
 */
export async function canUseChannelAnalysis(userId) {
  if (!userId) {
    return {
      allowed: false,
      error: 'User ID is required',
    };
  }

  try {
    const plan = await getUserPlan(userId);
    const limits = getPlanLimits(plan);

    return {
      allowed: limits.channelAnalysisEnabled,
      error: limits.channelAnalysisEnabled
        ? null
        : `Channel analysis is only available on the Growth plan. Upgrade to unlock this feature.`,
    };
  } catch (error) {
    console.error('Error checking channel analysis access:', error);
    return {
      allowed: false,
      error: 'Failed to check plan limits. Please try again.',
    };
  }
}

/**
 * Checks if user can use recently analyzed feature
 * @param {string} userId - User ID
 * @returns {Promise<{allowed: boolean, error?: string}>}
 */
export async function canUseRecentlyAnalyzed(userId) {
  if (!userId) {
    return {
      allowed: false,
      error: 'User ID is required',
    };
  }

  try {
    const plan = await getUserPlan(userId);
    const limits = getPlanLimits(plan);

    return {
      allowed: limits.recentlyAnalyzedEnabled,
      error: limits.recentlyAnalyzedEnabled
        ? null
        : `Recently Analyzed tab is only available on Pro and Growth plans. Upgrade to unlock this feature.`,
    };
  } catch (error) {
    console.error('Error checking recently analyzed access:', error);
    return {
      allowed: false,
      error: 'Failed to check plan limits. Please try again.',
    };
  }
}

/**
 * Gets a user-friendly error message for plan limits
 * @param {string} feature - Feature name ('videos', 'favourites', 'channelAnalysis')
 * @param {string} plan - User's current plan
 * @returns {string} Error message
 */
export function getPlanLimitMessage(feature, plan) {
  const normalizedPlan = (plan || 'free').toLowerCase();
  const limits = getPlanLimits(normalizedPlan);

  switch (feature) {
    case 'videos':
      if (limits.maxVideos === Infinity) {
        return null; // No limit
      }
      return `You've reached the limit of ${limits.maxVideos} videos on the ${normalizedPlan} plan. Upgrade to Pro or Growth for unlimited videos.`;

    case 'favourites':
      if (limits.favouritesEnabled) {
        return null; // Feature enabled
      }
      return `Favourites are only available on Pro and Growth plans. Upgrade to unlock this feature.`;

    case 'channelAnalysis':
      if (limits.channelAnalysisEnabled) {
        return null; // Feature enabled
      }
      return `Channel analysis is only available on the Growth plan. Upgrade to unlock this feature.`;

    case 'recentlyAnalyzed':
      if (limits.recentlyAnalyzedEnabled) {
        return null; // Feature enabled
      }
      return `Recently Analyzed tab is only available on Pro and Growth plans. Upgrade to unlock this feature.`;

    default:
      return 'This feature is not available on your current plan.';
  }
}

/**
 * Gets upgrade suggestion message based on feature
 * @param {string} feature - Feature name
 * @returns {string} Upgrade suggestion
 */
export function getUpgradeSuggestion(feature) {
  switch (feature) {
    case 'videos':
      return 'Upgrade to Pro or Growth for unlimited video analysis.';
    case 'favourites':
      return 'Upgrade to Pro or Growth to use favourites.';
    case 'channelAnalysis':
      return 'Upgrade to Growth to unlock channel analysis.';
    case 'recentlyAnalyzed':
      return 'Upgrade to Pro or Growth to unlock Recently Analyzed tab.';
    default:
      return 'Upgrade your plan to unlock this feature.';
  }
}

